/**
 * Linked Excel Editor Agent - Schema
 * 
 * Defines the parameters for the LinkedExcelEditorAgent when called
 * as an agent-as-tool from the orchestrator.
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// TARGET FILE SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

export const TargetFileSchema = z.object({
    path: z.string().describe('Full file path to the Excel workbook'),
    workbookId: z.string().nullable().describe('Carousel workbook ID if known'),
    isOpen: z.boolean().describe('Whether the file is currently open in Excel'),
});

// ═══════════════════════════════════════════════════════════════════════════
// REQUIREMENT SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

export const RequirementItemSchema = z.object({
    id: z.string().describe('Unique identifier for this requirement (e.g., "A", "B", "1")'),
    requirement: z.string().describe('The requirement or task to accomplish'),
});

// ═══════════════════════════════════════════════════════════════════════════
// AGENT PARAMS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

export const LinkedExcelEditorParamsSchema = z.object({
    targetFile: TargetFileSchema.describe('The Excel file to edit'),
    requirements: z.array(RequirementItemSchema).min(1).describe('List of editing requirements'),
    uiDescription: z.string()
        .describe("Single-line UI description for top-of-card agent status display."),
    uiDescriptions: z.array(z.string())
        .optional()
        .describe("Optional list of UI description lines for the top section of agent-as-tool cards."),
    context: z.array(z.string()).optional().describe('Context IDs for additional information'),
    enableUserInterview: z.boolean().optional().default(false).describe(
        'Whether to enable User Interview Tool (only when called from TaskPane)'
    ),
});

// ═══════════════════════════════════════════════════════════════════════════
// ARTIFACT INFO TYPE
// ═══════════════════════════════════════════════════════════════════════════

/** Info about an artifact created during editing (for PowerPoint insertion) */
export interface ArtifactInfo {
    /** Artifact ID for retrieval and insertion */
    artifactId: string;
    /** Human-readable description of the artifact */
    description?: string;
    /** Type of artifact */
    artifactType: "chart" | "range";
    /** Source sheet name */
    sourceSheet: string;
    /** Source reference (chart name or cell range) */
    sourceRef: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// RESULT DATA TYPE
// ═══════════════════════════════════════════════════════════════════════════

/** Result data specific to Excel editing */
export interface LinkedExcelResultData {
    /** Path to the edited clone file */
    clonePath: string;
    /** Session ID for the editing session */
    sessionId: string;
    /** Summary of changes made */
    changesSummary: string;
    /** Number of cells modified */
    cellsModified: number;
    /** Whether changes should be applied */
    applyChanges: boolean;
    /** List of artifacts created (for PowerPoint insertion) */
    artifacts?: ArtifactInfo[];
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export type TargetFile = z.infer<typeof TargetFileSchema>;
export type RequirementItem = z.infer<typeof RequirementItemSchema>;
export type LinkedExcelEditorParams = z.infer<typeof LinkedExcelEditorParamsSchema>;
